import { Component, ElementRef, EventEmitter, forwardRef, Inject, Input, OnDestroy, OnInit, Optional, Output, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { InterviewService, MonitoringService } from '@App';
import { ActionContext } from 'app/action-context';
import { InterviewDTO_extended } from '@DTOs';
import { InterviewManagerService } from '@Interview';
import { ConfirmationDialogService, InterviewDataService } from '@Services';
import { SubSink } from 'subsink';

import { InterviewSectionDialogAction, InterviewSectionDialogData, InterviewSectionDialogResponse } from './models';
import { SharedModule } from '@Shared/shared.module';
import { DynamicFormComponent } from '@Components/form-fields/dynamic-form/dynamic-form.component';
import { ThirdPartySection } from '@Components/third-party-section/third-party-section.component';
import { ThirdPartyNavigationDirection, ThirdPartySectionService } from '@Components/third-party-section/third-party-section.service';
import { InterviewMode } from '@Enums';


@Component({
    selector: 'interview-section-dialog',
    templateUrl: './interview-section-dialog.component.html',
    styleUrls: ['./interview-section-dialog.component.scss'],
    imports: [SharedModule, forwardRef(() => DynamicFormComponent), ThirdPartySection]
})
export default class InterviewSectionDialog implements OnInit, OnDestroy {
    @ViewChild('sectionContainer') sectionContainer: ElementRef;

    @Input() sectionId: string;
    @Input() title: string;
    @Input() client: any;
    @Input() account: any;
    @Input() case: any;
    @Input() saveButtonLabel: string = 'Save Decision';
    @Input() closeButtonLabel = 'Close';
    @Input() applicationSectionEdit = false;
    @Input() canSave = true;
    // Continues the action that opened this section so its load calls are attributed to it.
    @Input() actionId: string;
    // When set, saving runs as an action with this name.
    @Input() saveActionName: string;
    @Output() onSectionSaved = new EventEmitter<void>();
    @Output() onDialogClosed = new EventEmitter<void>();

    dialogTitle = 'Section Requirement';
    interviewLoaded = false;
    saving = false;
    sectionContainerHtmlId = 'interview-section-content';
    isModal = true;
    private destroyed = false;

    subs = new SubSink();

    constructor(                
        private interviewService: InterviewService,
        private interviewDataService: InterviewDataService,
        private monitoringService: MonitoringService,
        protected interviewManager: InterviewManagerService,
        private thirdPartySectionService: ThirdPartySectionService,
        private confirmationDialogService: ConfirmationDialogService,
        @Optional() public dialogRef?: MatDialogRef<InterviewSectionDialog, InterviewSectionDialogResponse>,
        @Optional() @Inject(MAT_DIALOG_DATA) public data?: InterviewSectionDialogData,
    ) {
        if ( this.data) {
            this.interviewService.setAccount(this.data.account);
            this.saveButtonLabel = data.saveButtonLabel;
        }else{
            this.isModal = false;
        }
    }


    //#region Subscriptions

    subscribeInterviewData() {
        const interview$ = this.applicationSectionEdit
            ? this.interviewDataService.reloadInterviewForSection(this.data.case.id, this.data.sectionId, false, true)
            : this.interviewDataService.reloadInterviewForSection(this.data.case.id, this.data.sectionId);
        this.subs.add(interview$.subscribe({
            next: interview => {
                // override active section with the specific section we want to show
                const interviewWithActiveSection: InterviewDTO_extended = {
                    ...interview,
                    activeSectionId: this.data.sectionId,
                    sections: (interview.sections ?? []).map(section => ({
                        ...section,
                        questions: section.questions ?? []
                    }))
                };
                this.interviewService.setInterviewData(interviewWithActiveSection);
                this.interviewManager.formatSectionData(interviewWithActiveSection);
                this.interviewLoaded = true;
            },
            error: error => this.interviewService.handleError(error)
        }));
    }

    subscribeToSavedInterviewResponse() {
        this.subs.add(this.interviewService.updatedInterview.subscribe(interview => {
            this.saving = false;
            const interviewWithActiveSection: InterviewDTO_extended = { 
                ...interview, 
                activeSectionId: this.data.sectionId,
                sections: (interview.sections ?? []).map(section => ({
                    ...section,
                    questions: section.questions ?? []
                }))
            };
            this.interviewService.setInterviewData(interviewWithActiveSection);
            this.interviewManager.formatSectionData(interviewWithActiveSection);
            this.interviewLoaded = true;

            const anyInterviewErrors = this.interviewService.checkForSectionErrors(this.data.sectionId, interviewWithActiveSection.sections);
            if (!anyInterviewErrors || (!this.applicationSectionEdit && interviewWithActiveSection.workflowCompleted)) {
                if (this.dialogRef) {
                this.dialogRef.close({
                    action: InterviewSectionDialogAction.Saved
                });
                }else{
                    this.onSectionSaved.emit();
                }
            }
        }));
    }

    subscribeToSavedInterviewError() {
        this.subs.add(this.interviewService.notifyUpdateInterviewError.subscribe(_ => {
            this.interviewLoaded = true;
            this.saving = false;
        }));
    }
    //#endregion
    //#region Lifecycle

    async ngOnInit() {
        if (!this.data){
            this.data = {
                account: this.account,
                case: this.case,
                client: this.client,
                sectionId: this.sectionId,
                title: this.title,
                saveButtonLabel: this.saveButtonLabel,
                actionId: this.actionId,
                saveActionName: this.saveActionName
            };

            this.interviewService.setAccount(this.data.account);
        }

        this.dialogTitle = this.data.title;

        if (this.applicationSectionEdit) {
            this.interviewService.isReadOnly = false;
            this.interviewManager.interviewMode = InterviewMode.Existing;
            this.subs.add(this.thirdPartySectionService.navigationFromThirdPartySectionObservable.subscribe(navigation => {
                if (navigation.direction === ThirdPartyNavigationDirection.Next) {
                    this.interviewManager.calledFromThirdParty = true;
                    this.interviewLoaded = true;
                    this.saving = false;
                    void this.handleSave();
                }
            }));
        }

        // Only the calls this dialog makes directly belong to the action; SignalR and subscription handlers do not.
        await ActionContext.resume(this.data.actionId, async () => {
            await this.interviewManager.init(this.data.client, this.data.account, this.data.case, this.sectionContainerHtmlId);
            if (this.destroyed) {
                this.interviewManager.subs.unsubscribe();
                return;
            }
            await this.interviewService.loadAccountRules(this.data.account.id);
        });
        if (this.destroyed) return;

        this.interviewManager.setupSignalRConnection();

        // The section load is triggered by this subscription, so it belongs to the action.
        ActionContext.resume(this.data.actionId, () => this.subscribeInterviewData());

        this.subscribeToSavedInterviewResponse();
        this.subscribeToSavedInterviewError();
    }

    ngOnDestroy() {
        this.destroyed = true;
        this.subs.unsubscribe();
        if (this.applicationSectionEdit) {
            this.interviewService.isReadOnly = true;
            this.interviewManager.subs.unsubscribe();
            void this.interviewManager.signalrConnection?.stop();
        }
    }

    //#endregion
    //#region Handlers

    handleClose() {
        if (this.applicationSectionEdit && this.saving) return;
        if (this.applicationSectionEdit && this.hasUnsavedChanges) {
            this.confirmationDialogService.open({
                title: 'Discard changes?',
                message: 'Your unsaved changes will be discarded. Actions already triggered during editing cannot be undone.',
                showCancel: true,
                okLabel: 'Discard changes',
                cancelLabel: 'Keep editing',
                onOk: () => this.closeEditor()
            });
            return;
        }
        this.closeEditor();
    }

    get hasUnsavedChanges(): boolean {
        return [
            this.interviewService.getDynamicForm(),
            ...Object.values(this.interviewService.reflexiveForms ?? {}),
            ...Object.values(this.interviewService.repeatForms ?? {}),
            ...Object.values(this.interviewService.healthForms ?? {}),
            ...Object.values(this.interviewService.repeatReflexiveForms ?? {})
        ].some(form => form?.dirty);
    }

    private closeEditor() {
        if (this.dialogRef) {
            this.dialogRef.close({
                action: InterviewSectionDialogAction.Canceled
            });
        }else{
            this.onDialogClosed.emit();
        }
    }

    async handleSave() {
        if (!this.canSave || !this.interviewLoaded || this.saving || this.interviewManager.formattedSection?.isLocked) return;
        if (this.data.saveActionName) {
            await this.monitoringService.runAction(this.data.saveActionName, this.getActionProperties(), () => this.save());
        } else {
            await this.save();
        }
    }

    private getActionProperties(): { [key: string]: string } {
        const { client, account, case: caseDetail } = this.data;
        const properties = {
            clientCode: client?.code,
            accountCode: account?.code,
            accountId: account?.id,
            caseId: caseDetail?.id,
            caseNumber: caseDetail?.caseNumberDisplay ?? caseDetail?.caseNumber
        };
        return properties;
    }

    private async save() {
        if (this.applicationSectionEdit && this.interviewManager.formattedSection.isThirdParty
            && !this.interviewManager.calledFromThirdParty) {
            await this.interviewManager.saveInterview(true);
            return;
        }

        this.saving = true;
        this.interviewLoaded = false;
        const didInterviewManagerDelegateToInterviewService = await this.interviewManager.saveInterview(this.applicationSectionEdit);

        if (didInterviewManagerDelegateToInterviewService === false) {
            this.interviewLoaded = true;
            this.saving = false;
        }
        // the dialog close will only happen when no errors are on the interview
        // See subscribeToSavedInterviewResponse function -> that code gets run when the interview.service.ts
        // updates an observable with the interview data after the save.
    }
    //#endregion
}
